"use strict";
/*******************************************************************************

    It is a class that provides balance check, transfer functions for one key pair.

    Copyright:
        Copyright (c) 2021 BOSAGORA Foundation
        All rights reserved.

    License:
        MIT License. See LICENSE for details.

*******************************************************************************/
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Wallet = void 0;
var Amount_1 = require("../common/Amount");
var KeyPair_1 = require("../common/KeyPair");
var Constant_1 = require("../data/Constant");
var Transaction_1 = require("../data/Transaction");
var TxInput_1 = require("../data/TxInput");
var TxOutput_1 = require("../data/TxOutput");
var Types_1 = require("../net/response/Types");
var Lock_1 = require("../script/Lock");
var TxBuilder_1 = require("../utils/TxBuilder");
var TxCanceller_1 = require("../utils/TxCanceller");
var TxPayloadFee_1 = require("../utils/TxPayloadFee");
var Utils_1 = require("../utils/Utils");
var Types_2 = require("./Types");
var WalletClient_1 = require("./WalletClient");
var WalletUTXOProvider_1 = require("./WalletUTXOProvider");
/**
 * It is a class that provides balance check, transfer functions for one key pair.
 */
var Wallet = /** @class */ (function () {
    /**
     * Constructor
     * @param owner     The key pair
     * @param option    The option of wallet
     */
    function Wallet(owner, option) {
        if (option === void 0) { option = Types_2.DefaultWalletOption(); }
        this.owner = owner;
        this.option = option;
        this.client = new WalletClient_1.WalletClient(this.option.endpoint);
        this.txBuilder = new TxBuilder_1.TxBuilder(this.owner);
        this.spendableUtxoProvider = new WalletUTXOProvider_1.WalletUTXOProvider(this.owner.address, this.client, Types_1.BalanceType.spendable);
        this.frozenUtxoProvider = new WalletUTXOProvider_1.WalletUTXOProvider(this.owner.address, this.client, Types_1.BalanceType.frozen);
    }
    /**
     * Check the balance
     */
    Wallet.prototype.getBalance = function () {
        return this.client.getBalance(this.owner.address);
    };
    /**
     * Transfer the BOA corresponding to the amount to the receiver.
     * The payload is the data to be stored in the block. A separate cost is incurred.
     * @param output_type The type of transaction output (0: OutputType.Payment, 1: OutputType.Freeze)
     * @param receiver  The array of recipient information
     * @param payload   The data to be stored, not used if not entered.
     * @private
     */
    Wallet.prototype._transfer = function (output_type, receiver, payload) {
        return __awaiter(this, void 0, void 0, function () {
            var payloadLength, payloadFee, freezingFee, sendBOA, outputCount, estimatedTxFee, totalFee, totalSpendAmount, utxosToSpend, utxo_res, e_1, tx, txSize, fees, fee_res, e_2, txFee, sumAmountUtxo, moreUtxosToSpend, utxo_res, e_3, fee_res, e_4, e_5;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (receiver.length === 0)
                            return [2 /*return*/, { code: Types_2.WalletResultCode.NotExistReceiver, message: Types_2.WalletMessage.NotExistReceiver }];
                        payloadLength = payload === undefined ? 0 : payload.length;
                        payloadFee = TxPayloadFee_1.TxPayloadFee.getFeeAmount(payloadLength);
                        freezingFee = output_type === TxOutput_1.OutputType.Freeze ? Constant_1.Constant.SlashPenaltyAmount : Amount_1.Amount.make(0);
                        sendBOA = receiver.reduce(function (sum, value) { return Amount_1.Amount.add(sum, value.amount); }, Amount_1.Amount.make(0));
                        outputCount = receiver.length + 1;
                        estimatedTxFee = Amount_1.Amount.make(Utils_1.Utils.FEE_RATE * Transaction_1.Transaction.getEstimatedNumberOfBytes(0, outputCount, payloadLength));
                        totalFee = Amount_1.Amount.add(Amount_1.Amount.add(payloadFee, estimatedTxFee), freezingFee);
                        totalSpendAmount = Amount_1.Amount.add(totalFee, sendBOA);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.spendableUtxoProvider.getUTXO(totalSpendAmount, Amount_1.Amount.make(Utils_1.Utils.FEE_RATE * TxInput_1.TxInput.getEstimatedNumberOfBytes()))];
                    case 2:
                        utxo_res = _a.sent();
                        if (utxo_res.code !== Types_2.WalletResultCode.Success || utxo_res.data === undefined) {
                            return [2 /*return*/, { code: utxo_res.code, message: utxo_res.message }];
                        }
                        utxosToSpend = utxo_res.data;
                        return [3 /*break*/, 4];
                    case 3:
                        e_1 = _a.sent();
                        return [2 /*return*/, { code: Types_2.WalletResultCode.FailedRequestUTXO, message: Types_2.WalletMessage.FailedRequestUTXO }];
                    case 4:
                        if (utxosToSpend.length === 0) {
                            return [2 /*return*/, { code: Types_2.WalletResultCode.NotEnoughAmount, message: Types_2.WalletMessage.NotEnoughAmount }];
                        }
                        try {
                            utxosToSpend.forEach(function (u) { return _this.txBuilder.addInput(u.type, u.utxo, u.amount); });
                            estimatedTxFee = Amount_1.Amount.make(Utils_1.Utils.FEE_RATE * Transaction_1.Transaction.getEstimatedNumberOfBytes(utxosToSpend.length, outputCount, payloadLength));
                            // Assign Payload
                            if (payload !== undefined)
                                this.txBuilder.assignPayload(payload);
                            // Build a transaction
                            receiver.forEach(function (m) { return _this.txBuilder.addOutput(m.address, m.amount); });
                            tx = this.txBuilder.sign(output_type, estimatedTxFee, payloadFee, freezingFee);
                        }
                        catch (e) {
                            return [2 /*return*/, { code: Types_2.WalletResultCode.FailedBuildTransaction, message: Types_2.WalletMessage.FailedBuildTransaction }];
                        }
                        txSize = tx.getNumberOfBytes();
                        _a.label = 5;
                    case 5:
                        _a.trys.push([5, 7, , 8]);
                        return [4 /*yield*/, this.client.getTransactionFee(txSize)];
                    case 6:
                        fee_res = _a.sent();
                        if (fee_res.code !== Types_2.WalletResultCode.Success || fee_res.data === undefined) {
                            return [2 /*return*/, { code: fee_res.code, message: fee_res.message }];
                        }
                        fees = fee_res.data;
                        return [3 /*break*/, 8];
                    case 7:
                        e_2 = _a.sent();
                        return [2 /*return*/, { code: Types_2.WalletResultCode.FailedRequestTxFee, message: Types_2.WalletMessage.FailedRequestTxFee }];
                    case 8:
                        txFee = fees.getFee(this.option.fee);
                        sumAmountUtxo = utxosToSpend.reduce(function (sum, n) { return Amount_1.Amount.add(sum, n.amount); }, Amount_1.Amount.make(0));
                        totalFee = Amount_1.Amount.add(payloadFee, txFee);
                        totalSpendAmount = Amount_1.Amount.add(totalFee, sendBOA);
                        _a.label = 9;
                    case 9:
                        if (!Amount_1.Amount.lessThan(sumAmountUtxo, totalSpendAmount)) return [3 /*break*/, 18];
                        moreUtxosToSpend = void 0;
                        _a.label = 10;
                    case 10:
                        _a.trys.push([10, 12, , 13]);
                        return [4 /*yield*/, this.spendableUtxoProvider.getUTXO(Amount_1.Amount.subtract(totalSpendAmount, sumAmountUtxo), Amount_1.Amount.make(Utils_1.Utils.FEE_RATE * TxInput_1.TxInput.getEstimatedNumberOfBytes()))];
                    case 11:
                        utxo_res = _a.sent();
                        if (utxo_res.code !== Types_2.WalletResultCode.Success || utxo_res.data === undefined) {
                            return [2 /*return*/, { code: utxo_res.code, message: utxo_res.message }];
                        }
                        moreUtxosToSpend = utxo_res.data;
                        return [3 /*break*/, 13];
                    case 12:
                        e_3 = _a.sent();
                        return [2 /*return*/, { code: Types_2.WalletResultCode.FailedRequestUTXO, message: Types_2.WalletMessage.FailedRequestUTXO }];
                    case 13:
                        if (moreUtxosToSpend.length === 0) {
                            return [2 /*return*/, { code: Types_2.WalletResultCode.NotEnoughAmount, message: Types_2.WalletMessage.NotEnoughAmount }];
                        }
                        // region Get the fee of new transaction
                        // Add new UTXOs
                        utxosToSpend.push.apply(utxosToSpend, moreUtxosToSpend);
                        try {
                            utxosToSpend.forEach(function (u) { return _this.txBuilder.addInput(u.type, u.utxo, u.amount); });
                            // Assign Payload
                            if (payload !== undefined)
                                this.txBuilder.assignPayload(payload);
                            // Build a transaction
                            receiver.forEach(function (m) { return _this.txBuilder.addOutput(m.address, m.amount); });
                            tx = this.txBuilder.sign(output_type, txFee, payloadFee, freezingFee);
                        }
                        catch (e) {
                            return [2 /*return*/, { code: Types_2.WalletResultCode.FailedBuildTransaction, message: Types_2.WalletMessage.FailedBuildTransaction }];
                        }
                        // Get the size of the transaction
                        txSize = tx.getNumberOfBytes();
                        _a.label = 14;
                    case 14:
                        _a.trys.push([14, 16, , 17]);
                        return [4 /*yield*/, this.client.getTransactionFee(txSize)];
                    case 15:
                        fee_res = _a.sent();
                        if (fee_res.code !== Types_2.WalletResultCode.Success || fee_res.data === undefined) {
                            return [2 /*return*/, { code: fee_res.code, message: fee_res.message }];
                        }
                        fees = fee_res.data;
                        return [3 /*break*/, 17];
                    case 16:
                        e_4 = _a.sent();
                        return [2 /*return*/, { code: Types_2.WalletResultCode.FailedRequestTxFee, message: Types_2.WalletMessage.FailedRequestTxFee }];
                    case 17:
                        // Select medium
                        txFee = fees.getFee(this.option.fee);
                        // endregion
                        sumAmountUtxo = utxosToSpend.reduce(function (sum, n) { return Amount_1.Amount.add(sum, n.amount); }, Amount_1.Amount.make(0));
                        totalSpendAmount = Amount_1.Amount.add(Amount_1.Amount.add(payloadFee, txFee), sendBOA);
                        return [3 /*break*/, 9];
                    case 18:
                        try {
                            utxosToSpend.forEach(function (u) { return _this.txBuilder.addInput(u.type, u.utxo, u.amount); });
                            // Assign Payload
                            if (payload !== undefined)
                                this.txBuilder.assignPayload(payload);
                            // Build a transaction
                            receiver.forEach(function (m) { return _this.txBuilder.addOutput(m.address, m.amount); });
                            tx = this.txBuilder.sign(output_type, txFee, payloadFee, freezingFee);
                        }
                        catch (e) {
                            return [2 /*return*/, { code: Types_2.WalletResultCode.FailedBuildTransaction, message: Types_2.WalletMessage.FailedBuildTransaction }];
                        }
                        _a.label = 19;
                    case 19:
                        _a.trys.push([19, 21, , 22]);
                        return [4 /*yield*/, this.client.sendTransaction(tx)];
                    case 20:
                        _a.sent();
                        return [3 /*break*/, 22];
                    case 21:
                        e_5 = _a.sent();
                        return [2 /*return*/, {
                                code: Types_2.WalletResultCode.FailedSendTx,
                                message: Types_2.WalletMessage.FailedSendTx,
                            }];
                    case 22: return [2 /*return*/, {
                            code: Types_2.WalletResultCode.Success,
                            message: Types_2.WalletMessage.Success,
                            data: tx,
                        }];
                }
            });
        });
    };
    /**
     * Transfer the BOA corresponding to the amount to the receiver.
     * The payload is the data to be stored in the block. A separate cost is incurred.
     * @param receiver  The array of recipient information
     * @param payload   The data to be stored, not used if not entered.
     */
    Wallet.prototype.transfer = function (receiver, payload) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this._transfer(TxOutput_1.OutputType.Payment, receiver, payload)];
            });
        });
    };
    /**
     * Among the transactions sent to Agora, it is a function to cancel
     * a transaction that is not yet included in the block.
     * This does not remove the previous transaction itself.
     * This creates a new transaction that transfers all UTXOs used
     * in the transaction to be canceled to the same address,
     * and sends it to Agora.
     * However, while it is being sent and processed, a previous transaction
     * that wants to be canceled may be included in the block first.
     * @param tx Previously sent the transaction to be canceled
     * @param key_finder A function that finds KeyPairs that can consume UTXOs and returns them to an array.
     */
    Wallet.prototype.cancel = function (tx, key_finder) {
        return __awaiter(this, void 0, void 0, function () {
            var utxos, utxo_res, e_6, key_pairs, canceller, res, e_7;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (tx.isCoinbase()) {
                            return [2 /*return*/, {
                                    code: Types_2.WalletResultCode.Cancel_NotAllowCoinbase,
                                    message: Types_2.WalletMessage.Cancel_NotAllowCoinbase,
                                }];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.client.getUTXOInfo(tx.inputs.map(function (m) { return m.utxo; }))];
                    case 2:
                        utxo_res = _a.sent();
                        if (utxo_res.code !== Types_2.WalletResultCode.Success || utxo_res.data === undefined) {
                            return [2 /*return*/, { code: utxo_res.code, message: utxo_res.message }];
                        }
                        utxos = utxo_res.data;
                        return [3 /*break*/, 4];
                    case 3:
                        e_6 = _a.sent();
                        return [2 /*return*/, { code: Types_2.WalletResultCode.FailedRequestUTXO, message: e_6.message }];
                    case 4:
                        if (key_finder !== undefined) {
                            key_pairs = key_finder(utxos
                                .filter(function (m) { return m.lock_type === Lock_1.LockType.Key; })
                                .map(function (u) { return new KeyPair_1.PublicKey(Buffer.from(u.lock_bytes, "base64")); }));
                        }
                        else {
                            key_pairs = [this.owner];
                        }
                        canceller = new TxCanceller_1.TxCanceller(tx, utxos, key_pairs);
                        res = canceller.build();
                        // Check for errors that occurred during the cancellation transaction creation process.
                        switch (res.code) {
                            case TxCanceller_1.TxCancelResultCode.Cancel_NotAllowUnfreezing:
                                return [2 /*return*/, {
                                        code: Types_2.WalletResultCode.Cancel_NotAllowUnfreezing,
                                        message: Types_2.WalletMessage.Cancel_NotAllowUnfreezing,
                                        data: res.tx,
                                    }];
                            case TxCanceller_1.TxCancelResultCode.Cancel_InvalidTransaction:
                                return [2 /*return*/, {
                                        code: Types_2.WalletResultCode.Cancel_InvalidTransaction,
                                        message: Types_2.WalletMessage.Cancel_InvalidTransaction,
                                        data: res.tx,
                                    }];
                            case TxCanceller_1.TxCancelResultCode.Cancel_NotFoundUTXO:
                                return [2 /*return*/, {
                                        code: Types_2.WalletResultCode.Cancel_NotFoundUTXO,
                                        message: Types_2.WalletMessage.Cancel_NotFoundUTXO,
                                        data: res.tx,
                                    }];
                            case TxCanceller_1.TxCancelResultCode.Cancel_UnsupportedLockType:
                                return [2 /*return*/, {
                                        code: Types_2.WalletResultCode.Cancel_UnsupportedLockType,
                                        message: Types_2.WalletMessage.Cancel_UnsupportedLockType,
                                        data: res.tx,
                                    }];
                            case TxCanceller_1.TxCancelResultCode.Cancel_NotFoundKey:
                                return [2 /*return*/, {
                                        code: Types_2.WalletResultCode.Cancel_NotFoundKey,
                                        message: Types_2.WalletMessage.Cancel_NotFoundKey,
                                        data: res.tx,
                                    }];
                            case TxCanceller_1.TxCancelResultCode.Cancel_NotEnoughFee:
                                return [2 /*return*/, {
                                        code: Types_2.WalletResultCode.Cancel_NotEnoughFee,
                                        message: Types_2.WalletMessage.Cancel_NotEnoughFee,
                                        data: res.tx,
                                    }];
                            case TxCanceller_1.TxCancelResultCode.FailedBuildTransaction:
                                return [2 /*return*/, {
                                        code: Types_2.WalletResultCode.FailedBuildTransaction,
                                        message: Types_2.WalletMessage.FailedBuildTransaction,
                                        data: res.tx,
                                    }];
                        }
                        if (!(res.code === TxCanceller_1.TxCancelResultCode.Success && res.tx !== undefined)) return [3 /*break*/, 9];
                        _a.label = 5;
                    case 5:
                        _a.trys.push([5, 7, , 8]);
                        return [4 /*yield*/, this.client.sendTransaction(res.tx)];
                    case 6:
                        _a.sent();
                        return [3 /*break*/, 8];
                    case 7:
                        e_7 = _a.sent();
                        return [2 /*return*/, {
                                code: Types_2.WalletResultCode.FailedSendTx,
                                message: Types_2.WalletMessage.FailedSendTx,
                            }];
                    case 8: return [2 /*return*/, {
                            code: Types_2.WalletResultCode.Success,
                            message: Types_2.WalletMessage.Success,
                            data: res.tx,
                        }];
                    case 9: return [2 /*return*/, {
                            code: Types_2.WalletResultCode.UnknownError,
                            message: Types_2.WalletMessage.UnknownError,
                        }];
                }
            });
        });
    };
    /**
     * Among the transactions sent to Agora, it is a function to cancel
     * a transaction that is not yet included in the block.
     * This does not remove the previous transaction itself.
     * This creates a new transaction that transfers all UTXOs used
     * in the transaction to be canceled to the same address,
     * and sends it to Agora.
     * However, while it is being sent and processed, a previous transaction
     * that wants to be canceled may be included in the block first.
     * @param tx_hash Previously sent the transaction hash to be canceled
     * @param key_finder A function that finds KeyPairs that can consume UTXOs and returns them to an array.
     */
    Wallet.prototype.cancelWithHash = function (tx_hash, key_finder) {
        return __awaiter(this, void 0, void 0, function () {
            var tx, res, e_8;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.client.getPendingTransaction(tx_hash)];
                    case 1:
                        res = _a.sent();
                        if (res.code !== Types_2.WalletResultCode.Success || res.data === undefined) {
                            return [2 /*return*/, { code: res.code, message: res.message }];
                        }
                        tx = res.data;
                        return [3 /*break*/, 3];
                    case 2:
                        e_8 = _a.sent();
                        return [2 /*return*/, {
                                code: Types_2.WalletResultCode.FailedRequestPendingTransaction,
                                message: Types_2.WalletMessage.FailedRequestPendingTransaction,
                            }];
                    case 3: return [2 /*return*/, this.cancel(tx, key_finder)];
                }
            });
        });
    };
    /**
     * Freeze the funds specified at the specified address.
     * @param receiver It is an object that has a receiving address and amount.
     */
    Wallet.prototype.freeze = function (receiver) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this._transfer(TxOutput_1.OutputType.Freeze, [receiver])];
            });
        });
    };
    /**
     * Unfreeze frozen UTXO.
     * @param utxos     Hashes of frozen UTXO
     * @param receiver  Public address to receive the unfreeze amount
     */
    Wallet.prototype.unfreeze = function (utxos, receiver) {
        return __awaiter(this, void 0, void 0, function () {
            var unspentTxOutputs, utxo_res, e_9, not_freeze, payloadFee, outputCount, sumOfUTXO, txSize, fees, fee_res, e_10, txFee, amount, tx, e_11;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.client.getUTXOInfo(utxos.map(function (m) { return m; }))];
                    case 1:
                        utxo_res = _a.sent();
                        if (utxo_res.code !== Types_2.WalletResultCode.Success || utxo_res.data === undefined) {
                            return [2 /*return*/, { code: utxo_res.code, message: utxo_res.message }];
                        }
                        unspentTxOutputs = utxo_res.data;
                        return [3 /*break*/, 3];
                    case 2:
                        e_9 = _a.sent();
                        return [2 /*return*/, { code: Types_2.WalletResultCode.FailedRequestUTXO, message: Types_2.WalletMessage.FailedRequestUTXO }];
                    case 3:
                        not_freeze = unspentTxOutputs.some(function (m) { return m.type !== TxOutput_1.OutputType.Freeze; });
                        if (not_freeze) {
                            return [2 /*return*/, {
                                    code: Types_2.WalletResultCode.Unfreeze_ExistNotFrozenUTXO,
                                    message: Types_2.WalletMessage.Unfreeze_ExistNotFrozenUTXO,
                                }];
                        }
                        payloadFee = Amount_1.Amount.make(0);
                        outputCount = 1;
                        sumOfUTXO = unspentTxOutputs.reduce(function (prev, u) { return Amount_1.Amount.add(Amount_1.Amount.add(prev, u.amount), Constant_1.Constant.SlashPenaltyAmount); }, Amount_1.Amount.make(0));
                        txSize = Transaction_1.Transaction.getEstimatedNumberOfBytes(unspentTxOutputs.length, outputCount, 0);
                        _a.label = 4;
                    case 4:
                        _a.trys.push([4, 6, , 7]);
                        return [4 /*yield*/, this.client.getTransactionFee(txSize)];
                    case 5:
                        fee_res = _a.sent();
                        if (fee_res.code !== Types_2.WalletResultCode.Success || fee_res.data === undefined) {
                            return [2 /*return*/, { code: fee_res.code, message: fee_res.message }];
                        }
                        fees = fee_res.data;
                        return [3 /*break*/, 7];
                    case 6:
                        e_10 = _a.sent();
                        return [2 /*return*/, { code: Types_2.WalletResultCode.FailedRequestTxFee, message: Types_2.WalletMessage.FailedRequestTxFee }];
                    case 7:
                        txFee = fees.getFee(this.option.fee);
                        amount = Amount_1.Amount.subtract(sumOfUTXO, txFee);
                        try {
                            unspentTxOutputs.forEach(function (u) { return _this.txBuilder.addInput(u.type, u.utxo, u.amount); });
                            this.txBuilder.addOutput(receiver, amount);
                            tx = this.txBuilder.sign(TxOutput_1.OutputType.Payment, txFee, payloadFee, Amount_1.Amount.make(0));
                        }
                        catch (e) {
                            return [2 /*return*/, { code: Types_2.WalletResultCode.FailedBuildTransaction, message: Types_2.WalletMessage.FailedBuildTransaction }];
                        }
                        _a.label = 8;
                    case 8:
                        _a.trys.push([8, 10, , 11]);
                        return [4 /*yield*/, this.client.sendTransaction(tx)];
                    case 9:
                        _a.sent();
                        return [3 /*break*/, 11];
                    case 10:
                        e_11 = _a.sent();
                        return [2 /*return*/, {
                                code: Types_2.WalletResultCode.FailedSendTx,
                                message: Types_2.WalletMessage.FailedSendTx,
                            }];
                    case 11: return [2 /*return*/, {
                            code: Types_2.WalletResultCode.Success,
                            message: Types_2.WalletMessage.Success,
                            data: tx,
                        }];
                }
            });
        });
    };
    /**
     * Returns an array of all frozen UTXOs for addresses already set
     */
    Wallet.prototype.getFrozenUTXOs = function (amount) {
        return __awaiter(this, void 0, void 0, function () {
            var frozenUtxos, utxo_res, e_12;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.frozenUtxoProvider.getUTXO(amount)];
                    case 1:
                        utxo_res = _a.sent();
                        if (utxo_res.code !== Types_2.WalletResultCode.Success || utxo_res.data === undefined) {
                            return [2 /*return*/, { code: utxo_res.code, message: utxo_res.message }];
                        }
                        frozenUtxos = utxo_res.data;
                        return [3 /*break*/, 3];
                    case 2:
                        e_12 = _a.sent();
                        return [2 /*return*/, { code: Types_2.WalletResultCode.FailedRequestUTXO, message: Types_2.WalletMessage.FailedRequestUTXO }];
                    case 3: return [2 /*return*/, {
                            code: Types_2.WalletResultCode.Success,
                            message: Types_2.WalletMessage.Success,
                            data: frozenUtxos,
                        }];
                }
            });
        });
    };
    return Wallet;
}());
exports.Wallet = Wallet;
//# sourceMappingURL=Wallet.js.map